import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

/**
 * FAQSectionShortcode - Renders a complete FAQ section with custom items
 * 
 * Usage in admin:
 * {{faq_section:"Section Title", "Question 1|Answer 1", "Question 2|Answer 2", "Question 3|Answer 3"}}
 * 
 * Or with default title:
 * {{faq_section:"", "Question 1|Answer 1", "Question 2|Answer 2"}}
 */

const FAQSectionShortcode = ({ params = [] }) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  // First parameter is the section title (optional)
  const sectionTitle = params[0] || t('faq.title');
  
  // Parse remaining parameters as Q&A pairs (format: "Question|Answer")
  const faqItems = params.slice(1).map(item => {
    const [question, answer] = item.split('|').map(s => s.trim());
    return { question, answer };
  }).filter(item => item.question && item.answer);

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (faqItems.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-8">
        <p className="text-yellow-800">
          <strong>FAQ Section:</strong> No questions provided. Format: 
          <code className="ml-2 bg-yellow-100 px-2 py-1 rounded text-sm">
            {`{{faq_section:"Title", "Question 1|Answer 1", "Question 2|Answer 2"}}`}
          </code>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF8F5] rounded-lg p-8 my-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#be1f1a] rounded-full mb-4">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-serif text-[#2C2420] mb-2">
            {sectionTitle}
          </h2>
          <p className="text-gray-600">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-200">
          {faqItems.map((item, index) => (
            <div key={index} className="p-6">
              <button
                onClick={() => toggleItem(index)}
                className="w-full flex items-start justify-between text-left gap-4 group"
              >
                <span className="text-lg font-medium text-[#2C2420] group-hover:text-[#be1f1a] transition-colors flex-1">
                  {item.question}
                </span>
                <span className="flex-shrink-0 mt-1">
                  {openIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-[#be1f1a]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-[#be1f1a] transition-colors" />
                  )}
                </span>
              </button>
              
              {openIndex === index && (
                <div className="mt-4 pr-8">
                  <div 
                    className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            {t('faq.footer')}{' '}
            <a href="/page/contact-us" className="text-[#be1f1a] hover:underline font-medium">
              {t('faq.contactUs')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQSectionShortcode;
